/**
 * Clock tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const clockTattoosPack: ToilePack = {
    slug: 'clock-tattoos',
    category: 'tattoo',
    name: 'Clock tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pocket', label: 'Pocket watch', subject: 'an open pocket watch on its chain, Roman numerals around the face, two roses beside it'},
        {key: 'roman', label: 'Roman clock', subject: 'a large clock face with Roman numerals and ornate hands, cracked around the rim, the hands at ten past ten'},
        {key: 'hourglass', label: 'Clock and hourglass', subject: 'an hourglass with sand falling, standing in front of a clock face whose hands point to the hour'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a key', 'grey wash', 'upper arm'],
    directionHint: 'try: a key',
    seo: {
        title: 'Clock tattoo designs: pocket watch, Roman clock, hourglass',
        metaDescription: 'Nine clock tattoo designs: a pocket watch with roses, a Roman-numeral clock face and a clock beside an hourglass, in three styles.',
        h1: 'Clock tattoo designs: pocket watch, Roman numeral clock, clock and hourglass',
        tagline: 'Three clocks, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Clock tattoo designs, which often mark a moment, a birth or a loss by the hour the hands point to. Worn on the forearm or the upper arm. The nine sheets show an open pocket watch with roses, a Roman-numeral clock face, and an hourglass in front of a clock. Each is drawn as traditional flash, fine line and dotwork. Type a different object or twist, such as a key, and the same styles can be asked for.',
        useCases: [
            'an upper-arm clock marking a date',
            'a pocket watch with flowers',
            'an hourglass and clock for the forearm',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine clock designs: a pocket watch with roses, a Roman-numeral clock and a clock with an hourglass, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I set the hands to a specific time?', a: 'Yes. Name the time or a twist, such as a key; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
